import React from "react";
import moment from "moment";
import { connect } from "react-redux";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { History } from "history";
import _ from "lodash";
import { FormattedMessage, injectIntl } from "react-intl";

import { PictureAsPdf, ArrowBack } from "components/Icons";
import Button from "components/Button";
import DetailView from "components/DetailView";
import { downloadDocument } from "helpers/download";
import DeliveryActions from "../actions";
import DeliveryLines from "./DeliveryLines";
import FustLines from "./FustLines";
import CostLines from "./CostLines";
import { ROLE_IDS } from "constants/.";

import "./style.scss";

interface IProps {
  match: any;
  history: History;
  location: any;
  getDelivery: (id: number, roleId: string) => void;
  getDeliveryLines: (id: number, roleId: string) => void;
  getFustLines: (id: number, roleId: string) => void;
  getCostLines: (id: number, roleId: string) => void;
  currentItem: any;
  deliveryLines: any[];
  fustLines: any[];
  costLines: any[];
  isLoading: boolean;
  intl: any;
}

const dashIfEmpty = value => (_.words(value).length > 0 ? value : "-");

class DeliveryDetails extends React.Component<IProps> {
  public componentDidMount() {
    const {
      match: {
        params: { id }
      },
      location: { search },
      getDelivery
    } = this.props;

    getDelivery(id, search);
  }

  public render() {
    const {
      getDeliveryLines,
      getFustLines,
      getCostLines,
      isLoading,
      currentItem,
      deliveryLines,
      fustLines,
      costLines
    } = this.props;

    if (!currentItem) {
      return null;
    }

    return (
      <div id="delivery-details-page">
        <h1>
          <ArrowBack onClick={() => this.props.history.back()} />
          <FormattedMessage id="DELIVERIES_DETAILS" />
        </h1>
        <div className="title-raw">
          <div className="title-raw-info">
            <div>
              <FormattedMessage id="DELIVERIES_NR" tagName="h3" />
              <h2>{currentItem.DeliveryId}</h2>
            </div>
            <div>
              <FormattedMessage id="DOCUMENT_DATE" tagName="h3" />
              <h2>{moment(currentItem.Date).format("DD-MM-YYYY")}</h2>
            </div>
          </div>
        </div>

        {this.renderDeliveryDetailsCard(currentItem)}

        {this.renderCompanyDetailsCard(currentItem)}

        <div className="download-button-wrapper">
          <Button
            id={`download-delivery-${_.get(
              currentItem,
              "DocumentId",
              "no_document"
            )}.pdf`}
            icon={<PictureAsPdf />}
            onClick={() => downloadDocument(currentItem)}
          >
            DELIVERIES_DOWNLOAD
          </Button>
        </div>

        <Tabs>
          <TabList>
            <Tab>
              <FormattedMessage id="DELIVERIES_LINES" />
            </Tab>
            <Tab>
              <FormattedMessage id="DELIVERIES_FUST_LINES" />
            </Tab>
            <Tab>
              <FormattedMessage id="DELIVERIES_EXTRA_COST" />
            </Tab>
          </TabList>
          <hr />
          <TabPanel>
            <DeliveryLines
              isLoading={isLoading}
              data={deliveryLines}
              loadData={() =>
                getDeliveryLines(currentItem.Id, currentItem.RoleId)
              }
            />
          </TabPanel>
          <TabPanel>
            <FustLines
              isLoading={isLoading}
              data={fustLines}
              loadData={() => getFustLines(currentItem.Id, currentItem.RoleId)}
            />
          </TabPanel>
          <TabPanel>
            <CostLines
              isLoading={isLoading}
              data={costLines}
              loadData={() => getCostLines(currentItem.Id, currentItem.RoleId)}
            />
          </TabPanel>
        </Tabs>
      </div>
    );
  }

  public renderDeliveryDetailsCard = currentItem => (
    <DetailView>
      <div className="card-row">
        <div className="card-column">
          <div className="card-item">
            <span className="card-item-label">
              <FormattedMessage id="PURCHASE_TYPE" />
            </span>
            <h3 className="card-item-value">{currentItem.RoleId}</h3>
          </div>
          <div className="card-item">
            <span className="card-item-label">
              <FormattedMessage id="DOCUMENT_DATE" />
            </span>
            <h3 className="card-item-value">
              {_.has(currentItem, "Date")
                ? moment(currentItem.Date).format("DD-MM-YYYY")
                : "-"}
            </h3>
          </div>
          <div className="card-item">
            <span className="card-item-label">
              <FormattedMessage id="DELIVERIES_BIO" />
            </span>
            <h3 className="card-item-value">
              <FormattedMessage id={currentItem.Bio ? "YES" : "NO"} />
            </h3>
          </div>
        </div>
        <div className="card-column">
          <div className="card-item">
            <span className="card-item-label">
              <FormattedMessage id="DELIVERIES_END_DATE" />
            </span>
            <h3 className="card-item-value">
              {_.has(currentItem, "Date")
                ? moment(currentItem.DeliveryDate).format("DD-MM-YYYY")
                : "-"}
            </h3>
          </div>
          <div className="card-item">
            <span className="card-item-label">
              <FormattedMessage id="DELIVERIES_CARRIER" />
            </span>
            <h3 className="card-item-value">
              {dashIfEmpty(currentItem.Transporter)}
            </h3>
          </div>
          <div className="card-item">
            <span className="card-item-label">
              <FormattedMessage id="ORDERS_SKAL" />
            </span>
            <h3 className="card-item-value">
              {dashIfEmpty(currentItem.SkalNumber)}
            </h3>
          </div>
        </div>
        <div className="card-column" />
      </div>
    </DetailView>
  );

  public renderCompanyDetailsCard = currentItem => {
    const {
      intl: { formatMessage }
    } = this.props;
    return (
      <DetailView
        renderExpanded={() => (
          <div className="card-row">
            <div className="card-column">
              <div className="card-item column">
                {currentItem.OtherRelation && (
                  <div className="card-item-value">
                    <h3>
                      {_.has(currentItem, "OtherRelation.Number")
                        ? `${formatMessage({ id: "RELATION_NR" })} ${
                            currentItem.OtherRelation.Number
                          }`
                        : "-"}
                    </h3>
                    <h3>
                      {_.has(currentItem, "OtherRelation.OfficeNumber")
                        ? `T. ${currentItem.OtherRelation.OfficeNumber}`
                        : "-"}
                    </h3>
                    <h3>
                      {_.has(currentItem, "OtherRelation.MailAddress")
                        ? `E. ${currentItem.OtherRelation.MailAddress}`
                        : "-"}
                    </h3>
                  </div>
                )}
              </div>
            </div>
            <div className="card-column">
              <div className="card-item column">
                {currentItem.Supplier && (
                  <div className="card-item-value">
                    <h3>
                      {_.has(currentItem, "Supplier.Number")
                        ? `${formatMessage({ id: "RELATION_NR" })} ${
                            currentItem.Supplier.Number
                          }`
                        : "-"}
                    </h3>
                    <h3>
                      {_.has(currentItem, "Supplier.OfficeNumber")
                        ? `T. ${currentItem.Supplier.OfficeNumber}`
                        : "-"}
                    </h3>
                    <h3>
                      {_.has(currentItem, "Supplier.MailAddress")
                        ? `E. ${currentItem.Supplier.MailAddress}`
                        : "-"}
                    </h3>
                  </div>
                )}
              </div>
            </div>
            <div className="card-column">
              <div className="cart-item column">
                {ROLE_IDS[currentItem.RoleId] === "Supplier" && (
                  <div className="card-item-value">
                    <h3>
                      {_.has(currentItem, "Seller.Number")
                        ? `${formatMessage({ id: "RELATION_NR" })} ${
                            currentItem.Seller.Number
                          }`
                        : "-"}
                    </h3>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      >
        <div className="card-row">
          <div className="card-column">
            <div className="card-item column">
              <span className="card-item-label">
                <FormattedMessage id="COUNTERPARTY" />
              </span>
              <h3 className="card-item-value">
                {dashIfEmpty(_.get(currentItem, "OtherRelation.Name", ""))}
              </h3>
            </div>
          </div>
          <div className="card-column">
            <div className="card-item column">
              <span className="card-item-label">
                <FormattedMessage id="PREFERRED_GROWER" />
              </span>
              <h3 className="card-item-value">
                {dashIfEmpty(_.get(currentItem, "Supplier.Name", ""))}
              </h3>
            </div>
          </div>
          <div className="card-column">
            <div className="card-item column">
              <span className="card-item-label">
                <FormattedMessage id="GROWER" />
              </span>
              <h3 className="card-item-value">
                {dashIfEmpty(
                  ROLE_IDS[currentItem.RoleId] === "Supplier"
                    ? _.get(currentItem, "Seller.Name", "")
                    : ""
                )}
              </h3>
            </div>
          </div>
        </div>
      </DetailView>
    );
  };
}

export default connect(
  state => ({
    currentItem: state.deliveries.currentItem,
    deliveryLines: state.deliveries.deliveryLines,
    fustLines: state.deliveries.fustLines,
    costLines: state.deliveries.costLines,
    isLoading: state.deliveries.isLoading
  }),
  {
    getDelivery: DeliveryActions.getDelivery,
    getDeliveryLines: DeliveryActions.getDeliveryLines,
    getFustLines: DeliveryActions.getFustLines,
    getCostLines: DeliveryActions.getCostLines
  }
)(injectIntl(DeliveryDetails));
